import React from 'react';
import { LangBlock } from '../../global';
import { SystemShared } from '../../../../shared/system';
import { UserDataWeb } from '../../../../shared/userdataweb';
import { CURRENCY_NAME } from '../../../../shared/currency';
import { PaymentType } from '../../../../shared/payments';
import { AdminDataTable } from './admin.table';
import { AdminPagination } from './admin.pagination';
import { usePaginatedTable } from './usePaginatedTable';
import { AdminPageWrap, AdminSection } from './admin.ui';

type PaymentRow = {
    sum: number;
    currency: CURRENCY_NAME;
    timestamp: number;
    type: PaymentType;
    user: number;
    id?: number;
};

type Props = {
    paymentsList: PaymentRow[];
    usersList: UserDataWeb[];
};

export const AdminPayments = ({ paymentsList, usersList }: Props) => {
    const [, , LangString] = LangBlock();
    const table = usePaginatedTable(paymentsList, 50);

    const userLabel = (id: number) => {
        const rec = usersList.find((q) => q.id === id);

        return rec ? `${rec.name} (${rec.id})` : `${id}`;
    };

    return (
        <AdminPageWrap>
            <AdminSection
                title={LangString('AdminPaymentsTitle', paymentsList.length)}
                subtitle={LangString('AdminPaymentsSubtitle')}
            >
                <AdminDataTable
                    data={table.pageData}
                    rowKey={(row, index) => row.id ?? `${row.timestamp}-${index}`}
                    emptyMessage={LangString('AdminNoPayments')}
                    columns={[
                        {
                            key: 'id',
                            label: LangString('AdminId'),
                            primary: true,
                            render: (row) => (row.id ? `#${row.id}` : '—'),
                        },
                        {
                            key: 'type',
                            label: LangString('WebDashboardBalanceHistoryPlatform'),
                            render: (row) => (
                                <img
                                    src={
                                        require(`../../../images/payment/${row.type}.svg`).default
                                    }
                                    height="25"
                                    alt={row.type}
                                />
                            ),
                        },
                        {
                            key: 'sum',
                            label: LangString('WebDashboardBalanceHistoryAmount'),
                            render: (row) => `${row.sum} ${row.currency}`,
                        },
                        {
                            key: 'timestamp',
                            label: LangString('WebDashboardBalanceHistoryDate'),
                            render: (row) => SystemShared.timeStampString(row.timestamp),
                        },
                        {
                            key: 'user',
                            label: LangString('AdminUser'),
                            render: (row) => userLabel(row.user),
                        },
                    ]}
                />
                <AdminPagination
                    page={table.page}
                    totalPages={table.totalPages}
                    total={table.total}
                    limit={table.limit}
                    limitOptions={[10, 30, 50, 100, 500, 1000, 10000]}
                    onPageChange={table.setPage}
                    onLimitChange={table.setLimit}
                />
            </AdminSection>
        </AdminPageWrap>
    );
};
